/**
 * The plan changes waiting on the reviewer, in the work pane (conversational
 * plan gate, Task 8).
 *
 * The plan page is read only to a person on purpose, so the way a plan improves
 * during review is that the rail PROPOSES a change and the reviewer disposes of
 * it here, between the document and the gate that approves it. Each proposal is
 * one card: the reason on top, then the diff of the sections it actually
 * touches, computed here from the page the pane already fetched and the
 * `proposedContent` snapshot that rides the open gate's payload.
 *
 * Two rules the shape enforces rather than states:
 *
 *   - the commit is PAGE-LEVEL. A card carries a quiet Reject and nothing else;
 *     one "Accept changes" applies everything still pending, in one server
 *     transaction, and the batch the reviewer sends is exactly the set on
 *     screen. A pending set that moved under them is a refusal, not a surprise.
 *   - the diff is a PENDING thing. It answers "what would this change?", so it
 *     lives only while that question is still open; an applied card is the
 *     reason and the stamp.
 *   - the material says the commitment state. The current line is print (the
 *     committed record) and the proposed line is glass (staged, waiting on a
 *     person); an applied card settles to print with a stamp before the pane
 *     re-reads the page and the gate.
 *
 * Staleness is not computed here. The server marks a change stale when the page
 * moved under it, and this renders that status honestly rather than guessing at
 * a hash the browser cannot verify.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check } from "lucide-react";
import type { PlanAmendmentDesign, WorkItemGate } from "@yagni/shared";
import { DESIGN_COPY } from "./designCopy.js";
import { HANDOFF_FLIP_MS } from "../decisions/consequence.js";
import { splitSections } from "./planMarkdown.js";
import {
  AMENDMENTS_LABEL,
  AMENDMENTS_NOTE,
  AMENDMENT_ACCEPT_EFFECT,
  AMENDMENT_ACCEPT_FAILED,
  AMENDMENT_ACCEPT_VERB,
  AMENDMENT_APPLIED_NOTE,
  AMENDMENT_APPLIED_STAMP,
  AMENDMENT_CURRENT_LABEL,
  AMENDMENT_DIFF_LOADING,
  AMENDMENT_DIFF_UNAVAILABLE,
  AMENDMENT_LAPSED_NOTE,
  AMENDMENT_OPENING_SECTION,
  AMENDMENT_PAGE_UNREADABLE,
  AMENDMENT_PROPOSED_LABEL,
  AMENDMENT_REJECTED_NOTE,
  AMENDMENT_REJECT_FAILED,
  AMENDMENT_REJECT_VERB,
  AMENDMENT_STALE_NOTE,
  AMENDMENT_UNCHANGED_NOTE,
  amendmentWhyLabel,
} from "./workCopy.js";

/* ── the payload the gate carries ─────────────────────────────────────────── */

export type PlanAmendmentStatus = "pending" | "accepted" | "rejected" | "stale" | "lapsed";

export interface PlanAmendmentCard {
  id: string;
  rationale: string;
  proposedContent: string | null;
  status: PlanAmendmentStatus;
  proposedAt: string;
  /**
   * Who proposed it, when the entry says. Optional and absent on every
   * rail-proposed entry, which is the majority: the rail writes the change on
   * a reader's behalf and has no author to name. A seeded entry from the
   * staff engineer's own read carries `"reeve"`, and that is the one case the
   * card labels differently.
   */
  proposedBy?: string;
  /** The design revision this change came with, when a chat revision moved the plan's design. */
  design?: PlanAmendmentDesign;
}

const CARD_STATUSES: ReadonlySet<string> = new Set(["pending", "stale", "lapsed"]);

/**
 * The gate's pending changes, tolerant of a payload written by a server that
 * knows more than this build does. An entry missing what a card needs is
 * dropped rather than rendered half-blank.
 */
export function readPlanAmendments(gate: WorkItemGate | null): PlanAmendmentCard[] {
  const raw = (gate?.payload as { planAmendments?: unknown } | null)?.planAmendments;
  if (!Array.isArray(raw)) return [];
  const out: PlanAmendmentCard[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const row = entry as Record<string, unknown>;
    if (typeof row.id !== "string" || typeof row.status !== "string") continue;
    if (typeof row.rationale !== "string" || typeof row.proposedAt !== "string") continue;
    out.push({
      id: row.id,
      rationale: row.rationale,
      proposedContent: typeof row.proposedContent === "string" ? row.proposedContent : null,
      status: row.status as PlanAmendmentStatus,
      proposedAt: row.proposedAt,
      // Kept when the entry names an author, dropped when it does not. Same
      // tolerance as every other field here: an unusable value is absence.
      ...(typeof row.proposedBy === "string" && row.proposedBy.trim()
        ? { proposedBy: row.proposedBy.trim() }
        : {}),
      ...designField(row.design),
    });
  }
  return out.sort((a, b) =>
    a.proposedAt === b.proposedAt ? a.id.localeCompare(b.id) : a.proposedAt < b.proposedAt ? -1 : 1,
  );
}

function designField(value: unknown): { design?: PlanAmendmentDesign } {
  const design = readDesign(value);
  return design ? { design } : {};
}

function readDesign(value: unknown): PlanAmendmentDesign | null {
  if (!value || typeof value !== "object") return null;
  const d = value as Record<string, unknown>;
  if (
    typeof d.prototypeId !== "string" ||
    typeof d.revision !== "number" ||
    !Number.isInteger(d.revision)
  )
    return null;
  return {
    prototypeId: d.prototypeId,
    revision: d.revision,
    previousRevision: typeof d.previousRevision === "number" ? d.previousRevision : null,
  };
}

/* ── the diff ─────────────────────────────────────────────────────────────── */

export type DiffRowKind = "ctx" | "add" | "del";
export interface DiffRow {
  kind: DiffRowKind;
  text: string;
}
export interface PlanSectionDiff {
  title: string;
  rows: DiffRow[];
}

/** Above this the quadratic walk is not worth it; the section reads as replaced. */
const MAX_DIFF_CELLS = 60000;

/** Blank edges are the page's spacing convention, never a change worth drawing. */
function trimEdges(lines: string[]): string[] {
  let start = 0;
  let end = lines.length;
  while (start < end && lines[start]!.trim() === "") start += 1;
  while (end > start && lines[end - 1]!.trim() === "") end -= 1;
  return lines.slice(start, end);
}

/** Longest-common-subsequence line diff. Small inputs by construction. */
function lineDiff(before: string[], after: string[]): DiffRow[] {
  const n = before.length;
  const m = after.length;
  if (n * m > MAX_DIFF_CELLS) {
    return [
      ...before.map((text): DiffRow => ({ kind: "del", text })),
      ...after.map((text): DiffRow => ({ kind: "add", text })),
    ];
  }
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      dp[i]![j] =
        before[i] === after[j] ? dp[i + 1]![j + 1]! + 1 : Math.max(dp[i + 1]![j]!, dp[i]![j + 1]!);
    }
  }
  const rows: DiffRow[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (before[i] === after[j]) {
      rows.push({ kind: "ctx", text: before[i]! });
      i += 1;
      j += 1;
    } else if (dp[i + 1]![j]! >= dp[i]![j + 1]!) {
      rows.push({ kind: "del", text: before[i]! });
      i += 1;
    } else {
      rows.push({ kind: "add", text: after[j]! });
      j += 1;
    }
  }
  while (i < n) rows.push({ kind: "del", text: before[i++]! });
  while (j < m) rows.push({ kind: "add", text: after[j++]! });
  return rows;
}

/**
 * The per-section unified diff between the page as it stands and the page as
 * the change would leave it. A section whose body is identical is left out
 * entirely: the sections an amendment never authors (Grounding, "How this plan
 * was made", the Amendments record) are byte-identical by contract, so drawing
 * them would bury the two lines that moved.
 */
export function planSectionDiffs(current: string, proposed: string): PlanSectionDiff[] {
  const currentSections = splitSections(current);
  const proposedSections = splitSections(proposed);
  const currentByTitle = new Map(currentSections.map((s) => [s.title, s]));
  const proposedByTitle = new Map(proposedSections.map((s) => [s.title, s]));

  const titles: string[] = proposedSections.map((s) => s.title);
  for (const section of currentSections) {
    if (!proposedByTitle.has(section.title)) titles.push(section.title);
  }

  const seen = new Set<string>();
  const diffs: PlanSectionDiff[] = [];
  for (const title of titles) {
    if (seen.has(title)) continue;
    seen.add(title);
    const before = trimEdges(currentByTitle.get(title)?.lines ?? []);
    const after = trimEdges(proposedByTitle.get(title)?.lines ?? []);
    if (before.join("\n") === after.join("\n")) continue;
    diffs.push({ title: title || AMENDMENT_OPENING_SECTION, rows: lineDiff(before, after) });
  }
  return diffs;
}

/* ── the cards ────────────────────────────────────────────────────────────── */

const ROW_MARK: Record<DiffRowKind, string> = { ctx: " ", add: "+", del: "-" };

/**
 * The page before and after, section by section: only the sections that
 * changed, each as a line diff. The cards draw a pending change this way, and
 * the plan's version line draws a version against the one before it
 * (`PlanVersionLine`), with its own side labels.
 */
export function SectionDiff({
  before,
  after,
  labels,
}: {
  before: string;
  after: string;
  /** The side labels: "now" and "proposed" on a card. */
  labels?: { removed: string; added: string };
}) {
  const diffs = useMemo(() => planSectionDiffs(before, after), [before, after]);
  return (
    <>
      {diffs.map((diff) => (
        <SectionDiffRows key={diff.title} diff={diff} labels={labels} />
      ))}
    </>
  );
}

function SectionDiffRows({
  diff,
  labels = { removed: AMENDMENT_CURRENT_LABEL, added: AMENDMENT_PROPOSED_LABEL },
}: {
  diff: PlanSectionDiff;
  labels?: { removed: string; added: string };
}) {
  return (
    <div className="amd-sect" data-testid="wk-amendment-section">
      <div className="amd-sect-h">{diff.title}</div>
      <div className="amd-lines">
        {diff.rows.map((row, i) => (
          <div
            key={`${row.kind}-${i}`}
            className={
              "amd-line " +
              (row.kind === "add" ? "is-new" : row.kind === "del" ? "is-cur" : "is-ctx")
            }
          >
            <span className="amd-mark" aria-hidden>
              {ROW_MARK[row.kind]}
            </span>
            <span className="amd-text">{row.text}</span>
            {row.kind !== "ctx" && (
              <span className="amd-side">{row.kind === "add" ? labels.added : labels.removed}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

type CardState = "pending" | "rejected" | "accepted" | "stale" | "lapsed";

function AmendmentCard({
  amendment,
  state,
  currentContent,
  contentLoading,
  rejecting,
  onReject,
  error,
}: {
  amendment: PlanAmendmentCard;
  state: CardState;
  currentContent: string | null;
  contentLoading: boolean;
  rejecting: boolean;
  onReject: () => void;
  error: string | null;
}) {
  const diffs = useMemo(() => {
    if (state !== "pending" || !amendment.proposedContent || currentContent == null) return [];
    return planSectionDiffs(currentContent, amendment.proposedContent);
  }, [state, amendment.proposedContent, currentContent]);

  // A diff belongs to a change still waiting on the reviewer. Once it is
  // applied the page HAS moved: the server drops the `proposedContent` snapshot
  // on the way out of pending, and the current side of the diff is now the
  // proposed side, so anything drawn here would be either empty or a line
  // apologizing for wording nobody needs any more. The applied card carries the
  // reason and the stamp, which is the whole honest record of what happened.
  const showDiff = state === "pending";

  return (
    <div
      className={
        "amd-card" +
        (state === "accepted" ? " settle-committed" : "") +
        (state === "rejected" || state === "lapsed" ? " is-muted" : "") +
        (state === "stale" ? " is-stale" : "")
      }
      data-testid="wk-amendment"
      data-amendment-id={amendment.id}
      data-state={state}
    >
      {/* The label names the author when the entry does. A change the staff
          engineer proposed off their own read is a different object from one
          the rail typed for the reader, and the card said "why" over both.
          An attributed label is a phrase where "why" was a word, so the row
          wraps and it takes its own line rather than squeezing the prose. */}
      <div
        className={"amd-why" + (amendment.proposedBy ? " is-attributed" : "")}
        data-proposed-by={amendment.proposedBy ?? undefined}
      >
        <span className="amd-why-lab" data-testid="wk-amendment-why-lab">
          {amendmentWhyLabel(amendment.proposedBy)}
        </span>
        <p className="amd-why-text">{amendment.rationale}</p>
      </div>
      {amendment.design && (
        <p className="amd-note" data-testid="wk-amendment-design">
          {DESIGN_COPY.amendmentDesign(amendment.design.revision)}
        </p>
      )}

      {showDiff &&
        (contentLoading && currentContent == null ? (
          <p className="amd-note">{AMENDMENT_DIFF_LOADING}</p>
        ) : amendment.proposedContent == null ? (
          <p className="amd-note">{AMENDMENT_DIFF_UNAVAILABLE}</p>
        ) : currentContent == null ? (
          <p className="amd-note is-warn" data-testid="wk-amendment-nopage">
            {AMENDMENT_PAGE_UNREADABLE}
          </p>
        ) : (
          <>
            {diffs.map((diff) => (
              <SectionDiffRows key={diff.title} diff={diff} />
            ))}
            {diffs.length > 0 && <p className="amd-note">{AMENDMENT_UNCHANGED_NOTE}</p>}
          </>
        ))}

      {state === "stale" && (
        <p className="amd-note is-warn" data-testid="wk-amendment-stale">
          {AMENDMENT_STALE_NOTE}
        </p>
      )}
      {state === "lapsed" && (
        <p className="amd-note" data-testid="wk-amendment-lapsed">
          {AMENDMENT_LAPSED_NOTE}
        </p>
      )}
      {state === "rejected" && (
        <p className="amd-note" data-testid="wk-amendment-rejected">
          {AMENDMENT_REJECTED_NOTE}
        </p>
      )}
      {state === "accepted" && (
        <div className="amd-stamp settle-stamp" data-testid="wk-amendment-stamp" role="status">
          <span className="amd-stamp-ic" aria-hidden>
            <Check width={12} height={12} strokeWidth={2.75} />
          </span>
          <span className="amd-stamp-word">{AMENDMENT_APPLIED_STAMP}</span>
          <span className="amd-stamp-note">{AMENDMENT_APPLIED_NOTE}</span>
        </div>
      )}

      {state === "pending" && (
        <div className="amd-acts">
          <button
            type="button"
            className="is-quiet amd-reject"
            onClick={onReject}
            disabled={rejecting}
          >
            {AMENDMENT_REJECT_VERB}
          </button>
        </div>
      )}
      {error && (
        <p className="amd-err" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export interface AmendmentCardsProps {
  workItemId: string;
  /** The item's OPEN gate. Only an execution-plan gate carries changes. */
  gate: WorkItemGate | null;
  /** The bound role-'plan' page, for the current side of the diff. */
  planPageId: string | null;
  /** Re-read the page and the gate: the pane owns both. */
  onChanged: () => void;
}

export function AmendmentCards({ workItemId, gate, planPageId, onChanged }: AmendmentCardsProps) {
  const amendments = useMemo(() => readPlanAmendments(gate), [gate]);
  /** Local settle: a card decided in this session keeps its answer on screen. */
  const [settled, setSettled] = useState<Record<string, "rejected" | "accepted">>({});
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [cardError, setCardError] = useState<Record<string, string>>({});
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [content, setContent] = useState<string | null>(null);
  const [contentLoading, setContentLoading] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current != null) clearTimeout(timer.current);
    },
    [],
  );

  const gateId = gate?.id ?? null;
  const hasCards = amendments.some((entry) => CARD_STATUSES.has(entry.status) || settled[entry.id]);

  // The current side of every diff. Re-read whenever the gate payload moves, so
  // a change proposed against a page this pane has not re-read since cannot
  // draw a diff nobody would recognize.
  const payloadKey = amendments.map((a) => `${a.id}:${a.status}`).join(",");
  useEffect(() => {
    if (!planPageId || !hasCards) {
      setContent(null);
      return;
    }
    let cancelled = false;
    setContentLoading(true);
    fetch(`/api/pages/${encodeURIComponent(planPageId)}`, { credentials: "include" })
      .then((res) => (res.ok ? (res.json() as Promise<{ content?: string }>) : null))
      .then((body) => {
        if (cancelled) return;
        setContent(typeof body?.content === "string" ? body.content : null);
      })
      .catch(() => {
        if (!cancelled) setContent(null);
      })
      .finally(() => {
        if (!cancelled) setContentLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planPageId, hasCards, payloadKey]);

  const cardState = useCallback(
    (entry: PlanAmendmentCard): CardState | null => {
      const local = settled[entry.id];
      if (local) return local;
      if (entry.status === "pending") return "pending";
      if (entry.status === "stale") return "stale";
      if (entry.status === "lapsed") return "lapsed";
      return null;
    },
    [settled],
  );

  const batch = useMemo(
    () => amendments.filter((entry) => cardState(entry) === "pending").map((entry) => entry.id),
    [amendments, cardState],
  );

  const reject = useCallback(
    async (amendmentId: string) => {
      if (!gateId) return;
      setRejecting(amendmentId);
      setCardError((prev) => {
        const next = { ...prev };
        delete next[amendmentId];
        return next;
      });
      try {
        const res = await fetch(
          `/api/work-items/${encodeURIComponent(workItemId)}/gates/${encodeURIComponent(
            gateId,
          )}/amendments/${encodeURIComponent(amendmentId)}/reject`,
          { method: "POST", credentials: "include" },
        );
        if (!res.ok) throw new Error(await errorText(res, AMENDMENT_REJECT_FAILED));
        setSettled((prev) => ({ ...prev, [amendmentId]: "rejected" }));
      } catch (err) {
        setCardError((prev) => ({
          ...prev,
          [amendmentId]: err instanceof Error ? err.message : AMENDMENT_REJECT_FAILED,
        }));
      } finally {
        setRejecting(null);
      }
    },
    [gateId, workItemId],
  );

  const accept = useCallback(async () => {
    if (!gateId || batch.length === 0) return;
    setAccepting(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/work-items/${encodeURIComponent(workItemId)}/gates/${encodeURIComponent(
          gateId,
        )}/amendments/accept`,
        {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ amendmentIds: batch }),
        },
      );
      if (!res.ok) {
        const message = await errorText(res, AMENDMENT_ACCEPT_FAILED);
        setError(message);
        // The server may have marked cards stale on the way to refusing, so the
        // pane re-reads the gate rather than leaving a diff that lies on screen.
        onChanged();
        return;
      }
      const applied: Record<string, "accepted"> = {};
      for (const id of batch) applied[id] = "accepted";
      setSettled((prev) => ({ ...prev, ...applied }));
      // Settle to print for one beat before the pane re-reads page and gate.
      timer.current = setTimeout(onChanged, HANDOFF_FLIP_MS);
    } catch (err) {
      setError(err instanceof Error ? err.message : AMENDMENT_ACCEPT_FAILED);
    } finally {
      setAccepting(false);
    }
  }, [batch, gateId, workItemId, onChanged]);

  // Proposed changes only. An answer to one of the plan's questions is shown
  // on the question it answers (`planQuestions.tsx`) and nowhere else: a
  // second card restating it read as a second thing to deal with, and there
  // was only ever one.
  if (!gate || gate.kind !== "approve_execution_plan") return null;
  if (!hasCards) return null;

  return (
    <div className="amd" data-testid="wk-amendments">
      {/* One label, and no explainer beside it: "nothing is applied until you
          accept" is a sentence about a fact the Accept verb below already
          shows, so it rides the label as its hover title (the one-line rule). */}
      <div className="amd-head">
        <span className="amd-lab" title={AMENDMENTS_NOTE} data-testid="wk-amendments-label">
          {AMENDMENTS_LABEL}
        </span>
        {/* A `title` on a non-interactive span reaches a mouse and nobody
            else, so the same sentence rides an sr-only span beside it. */}
        <span className="sr-only">{AMENDMENTS_NOTE}</span>
      </div>
      {amendments.map((entry) => {
        const state = cardState(entry);
        if (!state) return null;
        return (
          <AmendmentCard
            key={entry.id}
            amendment={entry}
            state={state}
            currentContent={content}
            contentLoading={contentLoading}
            rejecting={rejecting === entry.id}
            onReject={() => void reject(entry.id)}
            error={cardError[entry.id] ?? null}
          />
        );
      })}
      {batch.length > 0 && (
        <div className="amd-commit">
          <div className="tri-actions">
            <button
              type="button"
              className="deccard-go is-primary"
              onClick={() => void accept()}
              disabled={accepting}
              title={AMENDMENT_ACCEPT_EFFECT}
              data-testid="wk-amendments-accept"
            >
              {AMENDMENT_ACCEPT_VERB}
            </button>
          </div>
          {error && (
            <p className="amd-err" role="alert">
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** The server's own sentence when it sent one; the honest fallback otherwise. */
async function errorText(res: Response, fallback: string): Promise<string> {
  try {
    const body = (await res.json()) as { error?: unknown };
    if (typeof body.error === "string" && body.error.trim()) return body.error;
  } catch {
    // Fall through to the fallback.
  }
  return fallback;
}
